import { FC, useEffect } from "react";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

interface DownloadProps {
  url: string | null | undefined;
  isLoading: boolean;
  redirectToFile: (url: string) => void;
}

export const Download: FC<DownloadProps> = ({
  url,
  isLoading,
  redirectToFile,
}) => {
  useEffect(() => {
    if (url && !isLoading) {
      redirectToFile(url);
    }
  }, [url, isLoading, redirectToFile]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={3}
    >
      {isLoading && (
        <>
          <CircularProgress data-testid="export-download-progress" size={60} />
          <Typography variant="h6">Preparing your download...</Typography>
        </>
      )}

      {!url && !isLoading && (
        <>
          <Typography
            data-testid="export-file-not-found-heading"
            variant="h5"
            color="error"
          >
            File Not Found
          </Typography>
          <Typography variant="body1" color="textSecondary">
            The export file may have expired or does not exist.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Export files are available for 7 days.
          </Typography>
        </>
      )}

      {url && !isLoading && (
        <>
          <DownloadIcon sx={{ fontSize: 80, color: "primary.main" }} />
          <Typography variant="h6">
            Your download will start shortly...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            If the download doesn't start automatically:
          </Typography>
          <Button
            data-testid="export-download-fallback-btn"
            variant="contained"
            href={url}
            startIcon={<DownloadIcon />}
          >
            Click here to download
          </Button>
        </>
      )}
    </Box>
  );
};
